import { createElement, useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch, FiEdit, FiTrash2 } from "react-icons/fi";
import { IoIosAddCircleOutline } from "react-icons/io";

import { CompanyCreateEdit } from "@components/company/CompanyCreateEdit";
import { ICompanyDTO } from "@interfaces/ICompanyDTO";
import { Box } from "@mui/material";
import Input from "@components/Input";
import CompanyService from "@service/CompanyService";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { states } from "@utils/states";

const stateNameByCode: Record<string, string> = states.reduce(
  (acc, s) => ({ ...acc, [s.code]: s.value }),
  {} as Record<string, string>
);

export function CompanyList(): JSX.Element {
  const [companies, setCompanies] = useState<ICompanyDTO[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<ICompanyDTO | null>(
    null
  );

  const [company_id, setCompany_id] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const breakpoint = 768; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  useEffect(() => {
    async function fetchCompanies() {
      try {
        const data = await CompanyService.getAllCompanies();
        setCompanies(data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setIsLoading(false);
      }
    }
    if (isLoading) {
      fetchCompanies();
    }
  }, [isLoading]);

  const pageSize = 13;
  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
  };
  const [searchVal, setsearchVal] = useState("");

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      gridApi.setQuickFilter(val);
      setsearchVal(val);
    }
  };

  const handleEdit = (param: ICompanyDTO | null): void => {
    if (param !== null) {
      setCompany_id(param.id ?? 0);
      setSelectedCompany(param);
      setShowEditDialog(true);
    } else {
      setCompany_id(0);
      setSelectedCompany(null);
      setShowEditDialog(true);
    }
  };

  const handleDelete = async (company: ICompanyDTO): Promise<void> => {
    if (!company.id) return;
    if (!window.confirm(`Delete company "${company.name}"?`)) return;
    try {
      await CompanyService.deleteCompany(company.id);
      setIsLoading(true); // Refresh the list
    } catch (error) {
      console.error("Error deleting company:", error);
    }
  };

  const handleClose = () => {
    setShowEditDialog(false);
    setSelectedCompany(null);
    setCompany_id(-1);
    setIsLoading(true);
  };

  const columnDefs = [
    {
      headerName: "Company Id",
      field: "id",
      width: 120,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Name",
      field: "name",
      width: 280,
      filter: true,
    },
    {
      headerName: "Address",
      field: "address",
      width: 260,
      filter: true,
    },
    {
      headerName: "City",
      field: "city",
      width: 150,
      filter: true,
    },
    {
      headerName: "State",
      field: "state",
      width: 130,
      filter: true,
      valueFormatter: (params: any) =>
        params.value ? stateNameByCode[params.value] ?? params.value : "",
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Zip",
      field: "zip",
      width: 110,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Phone",
      field: "phone",
      width: 160,
      filter: true,
    },
    {
      headerName: "Edit",
      field: "action_edit",
      width: 65,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(params.data)}
          >
            {createElement(FiEdit as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Delete",
      field: "action_delete",
      width: 75,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-text-red-600 hover:tw-opacity-70"
            onClick={() => handleDelete(params.data)}
          >
            {createElement(FiTrash2 as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Add",
      field: "action_add",
      width: 65,
      cellRenderer: (): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(null)}
          >
            {createElement(
              IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
              { size: 16 }
            )}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-h-full">
      <Box
        sx={{
          padding: "1em",
        }}
      >
        <div className="tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h1 className="tw-color-primary">Companies</h1>
            <button
              className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700 tw-transition-colors"
              onClick={() => handleEdit(null)}
            >
              {createElement(
                IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
                { size: 20 }
              )}
              <span>Add Company</span>
            </button>
          </div>
          <div className="tw-flex tw-mb-4 tw-h-[3.5vh]">
            <Input
              icon={FiSearch}
              containerStyle={{ marginRight: 4 }}
              id="search"
              type="text"
              name="search"
              placeholder="search"
              onChange={(e: any) => updateSearchQuery(e.target.value)}
              disabled={false}
              value={searchVal}
            />
          </div>
          {!isLoading && (
            <>
              <div className="tw-mb-4" onContextMenu={handleContextMenu}>
                {!isMobile ? (
                  <>
                    <AgGridReact
                      className="ag-theme-balham"
                      domLayout="autoHeight"
                      columnDefs={columnDefs}
                      rowHeight={40}
                      rowData={companies}
                      gridOptions={{}}
                      rowSelection="multiple"
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      onCellContextMenu={handleCellContextMenu}
                      colResizeDefault="shift"
                      animateRows
                      pagination
                      pivotPanelShow="always"
                      paginationPageSize={pageSize}
                    />
                    {ContextMenuComponent}
                  </>
                ) : (
                  <div className="tw-space-y-4">
                    {companies
                      .filter((company) => {
                        if (searchVal.trim() === "") return true;
                        const searchWords = searchVal
                          .split(/\s+/)
                          .map((word) => word.trim().toLowerCase())
                          .filter(Boolean);
                        return searchWords.every((word) =>
                          `${company.name} ${company.city} ${company.state}`
                            .toLowerCase()
                            .includes(word)
                        );
                      })
                      .map((company) => (
                        <div
                          key={company.id}
                          className="tw-border tw-rounded-lg tw-p-4 tw-mb-4 tw-text-xs tw-shadow-md tw-leading-5"
                        >
                          <p>
                            <strong>Name:</strong> {company.name}
                          </p>
                          <p>
                            <strong>Address:</strong> {company.address}
                          </p>
                          <p>
                            <strong>City:</strong> {company.city}
                          </p>
                          <p>
                            <strong>State:</strong>{" "}
                            {company.state
                              ? stateNameByCode[company.state] ?? company.state
                              : ""}
                          </p>
                          <p>
                            <strong>Zip:</strong> {company.zip}
                          </p>
                          <p>
                            <strong>Phone:</strong> {company.phone}
                          </p>
                          <div className="tw-flex tw-gap-4 tw-mt-2 tw-justify-start">
                            <button
                              className="tw-flex tw-color-primary tw-border tw-rounded-lg tw-p-2 tw-w-20 tw-text-xs tw-bg-gray-200"
                              onClick={() => handleEdit(company)}
                            >
                              {createElement(
                                FiEdit as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span className="tw-ml-1">Edit</span>
                            </button>
                            <button
                              className="tw-flex tw-text-red-600 tw-border tw-rounded-lg tw-p-2 tw-w-20 tw-text-xs tw-bg-gray-200"
                              onClick={() => handleDelete(company)}
                            >
                              {createElement(
                                FiTrash2 as React.ComponentType<{
                                  size?: number;
                                }>,
                                { size: 16 }
                              )}
                              <span className="tw-ml-1">Delete</span>
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {showEditDialog && (
                <CompanyCreateEdit
                  id={company_id}
                  selectedCompany={selectedCompany}
                  isOpen={showEditDialog}
                  onClose={handleClose}
                />
              )}
            </>
          )}
        </div>
      </Box>
    </div>
  );
}
